import { ReactNode } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

export interface TabItem<K extends string = string> {
  key: K;
  label: ReactNode;
  count?: number;
  disabled?: boolean;
  'data-testid'?: string;
}

const Segmented = styled.div`
  ${tw`inline-flex items-center gap-1 p-1 rounded-lg max-w-full overflow-x-auto`}
  background-color: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
`;

const SegmentedButton = styled.button<{ $active: boolean }>`
  ${tw`inline-flex items-center gap-2 h-8 px-3 text-sm font-medium rounded-md border-0 cursor-pointer whitespace-nowrap`}
  color: ${({ $active }) =>
    $active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)'};
  background-color: ${({ $active }) => ($active ? 'var(--color-bg-elevated)' : 'transparent')};
  box-shadow: ${({ $active }) => ($active ? 'var(--shadow-xs)' : 'none')};
  transition:
    background-color var(--transition-normal),
    color var(--transition-normal);
  &:hover:not(:disabled) {
    color: var(--color-text-primary);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Underline = styled.div`
  ${tw`flex items-center gap-1 max-w-full overflow-x-auto`}
  border-bottom: 1px solid var(--color-border);
`;

const UnderlineButton = styled.button<{ $active: boolean }>`
  ${tw`relative inline-flex items-center gap-2 h-10 px-3 text-sm font-medium bg-transparent border-0 cursor-pointer whitespace-nowrap -mb-px`}
  color: ${({ $active }) =>
    $active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)'};
  border-bottom: 2px solid ${({ $active }) => ($active ? 'var(--color-black)' : 'transparent')};
  transition:
    color var(--transition-normal),
    border-color var(--transition-normal);
  &:hover:not(:disabled) {
    color: var(--color-text-primary);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Count = styled.span<{ $active: boolean }>`
  ${tw`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-semibold rounded-full`}
  background-color: ${({ $active }) =>
    $active ? 'rgb(var(--color-black-rgb) / 0.1)' : 'var(--color-bg-secondary)'};
  color: ${({ $active }) => ($active ? 'var(--color-black)' : 'var(--color-text-secondary)')};
`;

export function Tabs<K extends string>({
  items,
  value,
  onChange,
  variant = 'segmented',
  className,
  'aria-label': ariaLabel,
}: {
  items: TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  variant?: 'segmented' | 'underline';
  className?: string;
  'aria-label'?: string;
}) {
  const Root = variant === 'segmented' ? Segmented : Underline;
  const Item = variant === 'segmented' ? SegmentedButton : UnderlineButton;
  return (
    <Root role="tablist" aria-label={ariaLabel} className={className}>
      {items.map(item => {
        const active = item.key === value;
        return (
          <Item
            key={item.key}
            type="button"
            role="tab"
            aria-selected={active}
            $active={active}
            disabled={item.disabled}
            onClick={() => onChange(item.key)}
            data-testid={item['data-testid']}
          >
            {item.label}
            {typeof item.count === 'number' && <Count $active={active}>{item.count}</Count>}
          </Item>
        );
      })}
    </Root>
  );
}
